import { useEffect } from 'react';
import Widget from 'components/Dashboard/Widget';
import { useKPIGetHotelNights } from 'api/kpi/kpi';
import { KPIsWidgetEnum, LongTitles } from '../../utils';
import useOperationsKpiFilter from './useOperationsKpiFilter';
import {
  ControlFigures,
  nf,
  pct,
  tableStyle,
  tdNumStyle,
  tdStyle,
  thNumStyle,
  thStyle,
  totalRowStyle,
  WidgetState,
} from '../management/shared';

const per100 = (value: number | null | undefined) =>
  value === null || value === undefined ? '–' : nf(value, 1);

/**
 * Hotellnätter per 100 inköp/hemleveranser, per kundens län — var hotellnätterna är vanligast.
 * En natt = ett hotellkvitto per fälttestare och dag, se OperationsKpiLogic.GetHotelNights.
 */
const HotelNightsWidget = () => {
  const { isReady, isDateRangeTooLong, filter, deps } =
    useOperationsKpiFilter();
  const getData = useKPIGetHotelNights();

  useEffect(() => {
    if (isReady) {
      getData.mutate({ data: filter });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const result = getData.data;
  const counties = result?.byCounty ?? [];

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.HotelNights]}
      count={undefined}
      isLoading={getData.isLoading}
      info="Hotellnätter (hotellkvitton, en per fälttestare och dag) per 100 utförda inköp och hemleveranser, per kundens län: från-länet på inköp, till-länet på hemleverans. Nätter på andra ärendetyper visas som jämförelse och ingår inte i kvoten."
    >
      <WidgetState
        isLoading={getData.isLoading}
        isDateRangeTooLong={isDateRangeTooLong}
        isEmpty={
          !result ||
          (result.totalNights === 0 &&
            result.purchaseAndHomeDeliveryCases === 0)
        }
      >
        <p style={{ fontSize: '15px', margin: '4px 0 12px' }}>
          <strong>{per100(result?.nightsPer100Cases)}</strong> hotellnätter per
          100 inköp/hemleveranser · {nf(result?.totalNights ?? 0)} nätter
          totalt, {nf(result?.averageNightsPerDay ?? 0, 1)} per dygn
        </p>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Kundens län</th>
              <th style={thNumStyle}>Inköp + hemlev</th>
              <th style={thNumStyle}>Nätter</th>
              <th style={thNumStyle}>Per 100</th>
              <th style={thNumStyle}>Nätter övriga</th>
              <th style={thNumStyle}>Andel av alla nätter</th>
            </tr>
          </thead>
          <tbody>
            {counties.map((row) => (
              <tr
                key={row.countyName}
                style={{ borderBottom: '1px solid #eee' }}
              >
                <td style={tdStyle}>{row.countyName}</td>
                <td style={tdNumStyle}>
                  {nf(row.purchaseAndHomeDeliveryCases)}
                </td>
                <td style={tdNumStyle}>
                  {nf(row.purchaseAndHomeDeliveryNights)}
                </td>
                <td style={tdNumStyle}>{per100(row.nightsPer100Cases)}</td>
                <td style={tdNumStyle}>{nf(row.otherNights)}</td>
                <td style={tdNumStyle}>
                  {pct(row.shareOfAllNightsPercentage)}
                </td>
              </tr>
            ))}
            <tr style={totalRowStyle}>
              <td style={tdStyle}>Totalt</td>
              <td style={tdNumStyle}>
                {nf(result?.purchaseAndHomeDeliveryCases ?? 0)}
              </td>
              <td style={tdNumStyle}>
                {nf(result?.purchaseAndHomeDeliveryNights ?? 0)}
              </td>
              <td style={tdNumStyle}>{per100(result?.nightsPer100Cases)}</td>
              <td style={tdNumStyle}>{nf(result?.otherNights ?? 0)}</td>
              <td style={tdNumStyle}>
                {result?.totalNights ? '100,0 %' : '–'}
              </td>
            </tr>
          </tbody>
        </table>

        <ControlFigures
          items={[
            `${nf(result?.hotelReceipts ?? 0)} hotellkvitton`,
            `${nf(result?.plannedNightsWithoutReceipt ?? 0)} planerade hotellnätter utan kvitto räknas inte`,
          ]}
        />
      </WidgetState>
    </Widget>
  );
};

export default HotelNightsWidget;
